import { useSelector } from 'react-redux'

import { selectTerritories } from '@/features/domain/territory'
import { selectUserConfiguration } from '@/features/domain/user'

import { useRequestChangePlanModal } from '../../../modals/RequestChangePlanModal'

import { PaperStyle, PrimaryTagStyle } from '../../commonStyles'
import { dateFormats } from '../../formats'

import { Button, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { Tag } from '@workwave-tidal/core/components'

import { differenceInDays, format } from 'date-fns'

import { useTexts } from '../useTexts'

interface Props {
  planName: string
  expireDate: Date
  durationMonths: number
}

const countriesAllowedToRequestSms = [
  'US',
  'CA',
  'IT',
  'GB',
  'AU',
  'ES',
  'PT',
  'IE',
  'SE',
  'SA',
  'DK',
  'BE',
]

export function CurrentPlan(props: Props) {
  const { planName, expireDate, durationMonths } = props

  const texts = useTexts()

  const territories = useSelector(selectTerritories)

  const userConfiguration = useSelector(selectUserConfiguration)
  const date_format = dateFormats[userConfiguration.dateFormat]

  const daysToExpire = differenceInDays(expireDate, new Date()) + 1

  const { show: showRequestChangePlan, Modal: RequestChangePlanModal } = useRequestChangePlanModal()

  const countries = [...new Set(Object.values(territories).map(t => t.country))]
  const canChangePlan = countries.some(country => countriesAllowedToRequestSms.includes(country))

  const buttonDisabled = !canChangePlan

  return (
    <>
      <Paper elevation={0} sx={PaperStyle}>
        <Stack direction="column" spacing={2}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle3">{texts.currentPlanLabel}</Typography>
              <Stack direction="row" spacing={1}>
                <Tag sx={PrimaryTagStyle} label={planName} variant="outlined" color="primary" />
                <Tag
                  sx={PrimaryTagStyle}
                  label={texts.durationMonths(durationMonths)}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {texts.currentPlanDescription}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {texts.expiryDate}
            </Typography>
            <Typography variant="subtitle2">
              {texts.expiring(format(expireDate, date_format), daysToExpire)}
            </Typography>
          </Stack>

          <Tooltip
            title={buttonDisabled ? texts.changePlanDisabled : texts.changePlan}
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                onClick={showRequestChangePlan}
                data-testid="settings-communication-edit-plan-button"
                data-trackid="settings-communication-edit-plan-button"
                disabled={buttonDisabled}
                fullWidth
              >
                {texts.changePlan}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <RequestChangePlanModal />
    </>
  )
}
